import { useCallback, useEffect, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import { parseDate } from "shared/utils/dateUtils";

export interface BranchStat {
    id: string;
    name: string;
    teacherCount: number;
    studentCount: number;
    activeStudentCount: number;
    lessonCount: number;
    activeReleaseCount: number;
    unpaidCount: number;
    unpaidAmount: number;
    monthScore: number;
    word: boolean;
    sentence: boolean;
    video: boolean;
}

export interface OwnerStats {
    branchCount: number;
    teacherCount: number;
    studentCount: number;
    activeStudentCount: number;
    suspendedStudentCount: number;
    graduatedStudentCount: number;
    unpaidCount: number;
    unpaidAmount: number;
    activeReleaseCount: number;
    noticeCount: number;
    wordBranchCount: number;
    sentenceBranchCount: number;
    videoBranchCount: number;
    branches: BranchStat[];
}

const emptyStats: OwnerStats = {
    branchCount: 0,
    teacherCount: 0,
    studentCount: 0,
    activeStudentCount: 0,
    suspendedStudentCount: 0,
    graduatedStudentCount: 0,
    unpaidCount: 0,
    unpaidAmount: 0,
    activeReleaseCount: 0,
    noticeCount: 0,
    wordBranchCount: 0,
    sentenceBranchCount: 0,
    videoBranchCount: 0,
    branches: [],
};

interface BranchRow {
    id: string;
    name: string;
    word: boolean;
    sentence: boolean;
    video: boolean;
}

interface TeacherRow {
    id: string;
    branch_id: string;
}

interface StudentRow {
    id: string;
    branch_id: string;
    status: string;
    unpaied: number;
}

const docsOf = (res: any) => {
    if (!res || res.empty || !Array.isArray(res.docs)) return [];
    return res.docs;
};

const studentStatus = (status?: string) => status || "active";

const useOwnerStats = () => {
    const { readFirestore } = useFirestore();
    const [stats, setStats] = useState<OwnerStats>(emptyStats);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);
        const today = parseDate(new Date());
        const year = new Date().getFullYear();
        const month = new Date().getMonth() + 1;

        try {
            const [branchSnap, teacherSnap, studentSnap, noticeSnap] = await Promise.all([
                readFirestore("User/Auth/Branch"),
                readFirestore("User/Auth/Teacher"),
                readFirestore("User/Auth/Student"),
                readFirestore("Notice/Master/All"),
            ]);

            const branches: BranchRow[] = docsOf(branchSnap).map((doc: any) => ({
                id: doc.id,
                name: doc.data().name || "이름 없음",
                word: Boolean(doc.data().report_permission?.word),
                sentence: Boolean(doc.data().report_permission?.sentence),
                video: Boolean(doc.data().report_permission?.video),
            }));

            const teachers: TeacherRow[] = docsOf(teacherSnap).map((doc: any) => ({
                id: doc.id,
                branch_id: doc.data().branch_id || "",
            }));

            const students: StudentRow[] = docsOf(studentSnap).map((doc: any) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    branch_id: data.branch_id || "",
                    status: studentStatus(data.status),
                    unpaied: Number(data.unpaied) || 0,
                };
            });

            const perBranch: BranchStat[] = await Promise.all(branches.map(async (branch) => {
                const [lessonSnap, releaseSnap, scoreSnap] = await Promise.all([
                    readFirestore(`Lesson/Branch/${branch.id}`),
                    readFirestore(`ReportAssignBranch/${branch.id}/Report`),
                    readFirestore(`TossScore/${branch.id}/${year}/${month}/Score`),
                ]);

                const branchStudents = students.filter(item => item.branch_id === branch.id);
                const activeStudents = branchStudents.filter(item => item.status === "active");
                const unpaidStudents = branchStudents.filter(item => item.unpaied > 0);
                let activeReleaseCount = 0;
                docsOf(releaseSnap).forEach((doc: any) => {
                    const range = doc.data().effective_date;
                    if (range?.start && range?.end && range.start <= today && today <= range.end) {
                        activeReleaseCount += 1;
                    }
                });
                const monthScore = docsOf(scoreSnap).reduce((sum: number, doc: any) => {
                    return sum + (Number(doc.data().score) || 0);
                }, 0);

                return {
                    id: branch.id,
                    name: branch.name,
                    teacherCount: teachers.filter(item => item.branch_id === branch.id).length,
                    studentCount: branchStudents.length,
                    activeStudentCount: activeStudents.length,
                    lessonCount: docsOf(lessonSnap).length,
                    activeReleaseCount,
                    unpaidCount: unpaidStudents.length,
                    unpaidAmount: unpaidStudents.reduce((sum: number, item: StudentRow) => sum + item.unpaied, 0),
                    monthScore,
                    word: branch.word,
                    sentence: branch.sentence,
                    video: branch.video,
                };
            }));

            perBranch.sort((a, b) => b.activeStudentCount - a.activeStudentCount || a.name.localeCompare(b.name, "ko"));

            const activeStudents = students.filter(item => item.status === "active");
            const unpaidStudents = students.filter(item => item.unpaied > 0);

            setStats({
                branchCount: branches.length,
                teacherCount: teachers.length,
                studentCount: students.length,
                activeStudentCount: activeStudents.length,
                suspendedStudentCount: students.filter(item => item.status === "suspended").length,
                graduatedStudentCount: students.filter(item => item.status === "graduated").length,
                unpaidCount: unpaidStudents.length,
                unpaidAmount: unpaidStudents.reduce((sum, item) => sum + item.unpaied, 0),
                activeReleaseCount: perBranch.reduce((sum, item) => sum + item.activeReleaseCount, 0),
                noticeCount: docsOf(noticeSnap).length,
                wordBranchCount: perBranch.filter(item => item.word).length,
                sentenceBranchCount: perBranch.filter(item => item.sentence).length,
                videoBranchCount: perBranch.filter(item => item.video).length,
                branches: perBranch,
            });
        } catch (error) {
            console.error(error);
            setIsError(true);
            setStats(emptyStats);
        } finally {
            setIsLoading(false);
        }
    }, [readFirestore]);

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    return { stats, isLoading, isError, onRefresh: load };
};

export default useOwnerStats;
